import { useCallback, useMemo, useState } from "react";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import MultiSelect from "@/components/MultiSelect";
import SearchInput from "@/components/SearchInput";
import Table, { TableControls, TableFooter } from "@/components/Table";
import { useCanViewCommitteesBool } from "@/hooks/useCapabilities";
import { useRowHighlight } from "@/hooks/useRowHighlight";
import { useParams as useUrlParams, useSearchWithParams } from "@/hooks/useSearchParams";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { CommitteeAssignment } from "@/types";
import { useQuery } from "@apollo/client";
import type { SortingState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import {
  getCommitteeHistoryColumns,
  type CommitteeAssignmentRow,
  type CommitteeStatus
} from "./_components/CommitteeHistoryTableColumns";
import { GET_COMMITTEE_HISTORY_QUERY } from "../person/queries";
import { committeeHistoryHighlightId } from "../../membership-services/legacy-committees/committeeHistoryHighlight";
import {
  ASSIGNMENT_DISPLAY_STATUS_LABELS,
  getAssignmentDisplayStatus
} from "../../membership-services/committees/assignmentDisplayStatus";
import {
  COMMITTEES_ALL_PATH,
  isNationalSection,
  sectionOperationsCommitteesPath
} from "../../membership-services/committees/constants";
import { Path, useNavigate } from "@/router";

const DEFAULT_SORTING: SortingState = [{ id: "endDate", desc: true }];

const STATUS_OPTIONS: { value: CommitteeStatus; label: string }[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Planned", label: "Planned" }
];

function committeeHistoryDisplayStatus(
  assignment: { startDate?: string | null; endDate?: string | null },
  todayIso: string
): CommitteeStatus {
  return ASSIGNMENT_DISPLAY_STATUS_LABELS[
    getAssignmentDisplayStatus(
      { startDate: assignment.startDate ?? null, endDate: assignment.endDate ?? null },
      todayIso
    )
  ];
}

function parseStringArrayParam(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

function formatTableData(list: CommitteeAssignment[], todayIso: string): CommitteeAssignmentRow[] {
  return list.map((e: CommitteeAssignment) => ({
    id: committeeHistoryHighlightId({
      committeeName: e.committeeName,
      position: e.position,
      startDate: e.startDate,
      endDate: e.endDate ?? null
    }),
    committeeName: e.committeeName,
    position: e.position,
    startDate: e.startDate ?? "",
    endDate: e.endDate ?? null,
    status: committeeHistoryDisplayStatus(e, todayIso),
    section: e.section
  }));
}

function matchesCommitteeNameSearch(row: CommitteeAssignmentRow, search: string) {
  if (!search) return true;
  return row.committeeName.toLowerCase().includes(search.toLowerCase());
}

export default function CommitteeDetails() {
  const { pgaId } = useParams();
  const navigate = useNavigate();
  const canViewCommittees = useCanViewCommitteesBool();
  const { Menu, setMenu, reposition } = useTableRowMenu();
  const { params, addSearchParam, deleteSearchParams } = useUrlParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "committeeName"
  });
  const [sortingState, setSortingState] = useState<SortingState>(DEFAULT_SORTING);
  const selectedStatusValues = useMemo(
    () => parseStringArrayParam(params.status) as CommitteeStatus[],
    [params.status]
  );

  const { data, loading, error } = useQuery(GET_COMMITTEE_HISTORY_QUERY, {
    variables: { pgaId: pgaId! },
    fetchPolicy: "cache-first"
  });

  const committeeHistory = data?.person?.committeeHistory;
  const todayIso = DateTime.utc().toISODate() ?? "";
  const tableData = useMemo(
    () => (committeeHistory?.length ? formatTableData(committeeHistory, todayIso) : []),
    [committeeHistory, todayIso]
  );
  const filteredData = useMemo(
    () =>
      tableData
        .filter((row) => matchesCommitteeNameSearch(row, debouncedSearch))
        .filter((row) => selectedStatusValues.length === 0 || selectedStatusValues.includes(row.status)),
    [tableData, debouncedSearch, selectedStatusValues]
  );
  const activeFilterCount = [!!debouncedSearch, selectedStatusValues.length > 0].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;
  const { highlightRowId, containerRef } = useRowHighlight({
    dataReady: !loading && filteredData.length > 0
  });

  function setSelectedStatuses(statuses: CommitteeStatus[]) {
    if (statuses.length > 0) {
      addSearchParam("status[]", statuses.join(","));
    } else {
      deleteSearchParams("status[]");
    }
  }

  function clearAllFilters() {
    clearSearch();
    setSelectedStatuses([]);
  }

  const handleViewCommittee = useCallback(
    (row: CommitteeAssignmentRow) => {
      const searchParams = new URLSearchParams({
        highlightRow: row.committeeName
      });

      if (row.section && !isNationalSection(row.section)) {
        navigate(`${sectionOperationsCommitteesPath(row.section.id)}?${searchParams.toString()}` as Path);
        return;
      }

      navigate(`${COMMITTEES_ALL_PATH}?${searchParams.toString()}` as Path);
    },
    [navigate]
  );
  const committeeHistoryColumns = useMemo(
    () =>
      getCommitteeHistoryColumns({
        ...(canViewCommittees && {
          setMenu,
          onViewCommittee: handleViewCommittee
        })
      }),
    [canViewCommittees, handleViewCommittee, setMenu]
  );

  if (error) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Committee History"
        message={error.message}
        className="mt-12"
      />
    );
  }

  return (
    <div ref={containerRef}>
      <div className="pt-12" />
      <Table
        data={filteredData}
        loading={loading}
        columns={committeeHistoryColumns}
        sortingState={sortingState}
        onSortingChange={setSortingState}
        hasActiveFilters={hasActiveFilters}
        getRowClasses={
          highlightRowId ? (row) => (row.original.id === highlightRowId ? "row-highlight" : "") : undefined
        }
        renderControls={
          tableData.length > 0
            ? (_table, { onSortClick } = {}) => (
                <TableControls
                  useExpandedFilters
                  expandedFiltersState={{ status: selectedStatusValues.length > 0 }}
                  activeFilters={activeFilterCount}
                  onClearAllFilters={clearAllFilters}
                  onSortClick={onSortClick}
                >
                  <SearchInput
                    data-name="committeeName"
                    placeholder="Search Committees"
                    query={searchValue}
                    onChange={setSearchValue}
                    onClear={clearSearch}
                  />
                  <div data-name="status">
                    <MultiSelect
                      label="Status"
                      selectedValues={selectedStatusValues}
                      className="w-full"
                      positioning="absolute"
                      options={STATUS_OPTIONS}
                      onSelect={(value, checked) => {
                        const statuses = checked
                          ? [...selectedStatusValues, value as CommitteeStatus]
                          : selectedStatusValues.filter((status) => status !== value);
                        setSelectedStatuses(statuses);
                      }}
                      onClear={() => {
                        setSelectedStatuses([]);
                      }}
                    />
                  </div>
                </TableControls>
              )
            : undefined
        }
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Committee Participation"
            caption="This person does not have committee participation history"
            iconName="group"
          />
        )}
        renderFooter={tableData.length > 0 ? () => <TableFooter totalRowCount={filteredData.length} /> : undefined}
        data-testid="committee-history-table"
        onHorizontalScroll={reposition}
      />
      <Menu />
    </div>
  );
}
